/**
 * Roland Jupiter-8 - Synthétiseur polyphonique légendaire (1981)
 *
 * Caractéristiques:
 * - 8 voix polyphoniques
 * - 2 VCO par voix (Saw, Pulse, Square, Sub)
 * - Filtre 24dB lowpass résonant + HPF
 * - 2 ADSR (Filter + Amp)
 * - LFO avec routage flexible
 * - Chorus stéréo emblématique
 */

import * as Tone from 'tone';

export class Jupiter8 {
    constructor(options = {}) {
        this.performance = options.performance || { lowPower: false };
        this.isPowered = true;
        this.mixerChannel = null;

        // Paramètres par défaut
        this._params = {
            // VCO 1
            vco1Wave: 'sawtooth',
            vco1Octave: 0,          // -2, -1, 0, +1, +2
            vco1Level: 0.7,
            vco1PulseWidth: 0.5,

            // VCO 2
            vco2Wave: 'sawtooth',
            vco2Octave: 0,
            vco2Level: 0.5,
            vco2Detune: 5,          // cents
            vco2PulseWidth: 0.5,

            // Sub oscillator
            subLevel: 0.3,

            // Noise
            noiseLevel: 0,

            // Filter
            filterCutoff: 2000,
            filterResonance: 2,
            filterEnvAmount: 0.5,
            hpfCutoff: 20,

            // Filter Envelope
            filterAttack: 0.01,
            filterDecay: 0.3,
            filterSustain: 0.5,
            filterRelease: 0.5,

            // Amp Envelope
            ampAttack: 0.01,
            ampDecay: 0.2,
            ampSustain: 0.7,
            ampRelease: 0.5,

            // LFO
            lfoRate: 2,
            lfoWave: 'triangle',
            lfoToPitch: 0,
            lfoToFilter: 0,
            lfoToPW: 0,

            // Chorus (emblématique du JP-8)
            chorusRate: 0.8,
            chorusDepth: 0.5,
            chorusMix: 0.5,

            // Master
            volume: 0.7,
            portamento: 0
        };

        this._createSynth();
    }

    _createSynth() {
        // LFO global
        this.lfo = new Tone.LFO({
            frequency: this._params.lfoRate,
            type: this._params.lfoWave,
            min: -1,
            max: 1
        }).start();

        // Gains pour modulation LFO
        this.lfoPitchGain = new Tone.Gain(0);
        this.lfoFilterGain = new Tone.Gain(0);
        this.lfoPWGain = new Tone.Gain(0);

        this.lfo.connect(this.lfoPitchGain);
        this.lfo.connect(this.lfoFilterGain);
        this.lfo.connect(this.lfoPWGain);

        // Créer le PolySynth avec une voix custom
        this.synth = new Tone.PolySynth(Tone.Synth, {
            maxPolyphony: 8,
            voice: Tone.Synth,
            options: {
                oscillator: {
                    type: 'sawtooth'
                },
                envelope: {
                    attack: this._params.ampAttack,
                    decay: this._params.ampDecay,
                    sustain: this._params.ampSustain,
                    release: this._params.ampRelease
                }
            }
        });

        // Deuxième oscillateur (layer)
        this.synth2 = new Tone.PolySynth(Tone.Synth, {
            maxPolyphony: 8,
            voice: Tone.Synth,
            options: {
                oscillator: {
                    type: 'sawtooth'
                },
                envelope: {
                    attack: this._params.ampAttack,
                    decay: this._params.ampDecay,
                    sustain: this._params.ampSustain,
                    release: this._params.ampRelease
                }
            }
        });

        // Sub oscillator (une octave en dessous)
        this.subSynth = new Tone.PolySynth(Tone.Synth, {
            maxPolyphony: 8,
            voice: Tone.Synth,
            options: {
                oscillator: {
                    type: 'square'
                },
                envelope: {
                    attack: this._params.ampAttack,
                    decay: this._params.ampDecay,
                    sustain: this._params.ampSustain,
                    release: this._params.ampRelease
                }
            }
        });

        // Noise
        this.noise = new Tone.Noise('white');
        this.noiseGain = new Tone.Gain(0);
        this.noiseEnv = new Tone.AmplitudeEnvelope({
            attack: this._params.ampAttack,
            decay: this._params.ampDecay,
            sustain: this._params.ampSustain,
            release: this._params.ampRelease
        });

        // Mixeur des oscillateurs
        this.vco1Gain = new Tone.Gain(this._params.vco1Level);
        this.vco2Gain = new Tone.Gain(this._params.vco2Level);
        this.subGain = new Tone.Gain(this._params.subLevel);

        // High-pass filter
        this.hpf = new Tone.Filter({
            frequency: this._params.hpfCutoff,
            type: 'highpass',
            rolloff: -12
        });

        // Low-pass filter (le cœur du son JP-8)
        this.lpf = new Tone.Filter({
            frequency: this._params.filterCutoff,
            type: 'lowpass',
            rolloff: -24,
            Q: this._params.filterResonance
        });

        // Filter envelope
        this.filterEnv = new Tone.Envelope({
            attack: this._params.filterAttack,
            decay: this._params.filterDecay,
            sustain: this._params.filterSustain,
            release: this._params.filterRelease
        });

        this.filterEnvGain = new Tone.Gain(this._params.filterEnvAmount * 5000);
        this.filterEnv.connect(this.filterEnvGain);
        this.filterEnvGain.connect(this.lpf.frequency);

        // Connecter LFO au filtre
        this.lfoFilterGain.connect(this.lpf.frequency);

        // Chorus emblématique du Jupiter-8
        this.chorus = new Tone.Chorus({
            frequency: this._params.chorusRate,
            delayTime: 3.5,
            depth: this._params.chorusDepth,
            wet: this._params.chorusMix
        }).start();

        // Volume de sortie
        this.outputGain = new Tone.Gain(this._params.volume);

        // Chaîne audio
        this.synth.connect(this.vco1Gain);
        this.synth2.connect(this.vco2Gain);
        this.subSynth.connect(this.subGain);

        this.noise.connect(this.noiseEnv);
        this.noiseEnv.connect(this.noiseGain);

        this.vco1Gain.connect(this.hpf);
        this.vco2Gain.connect(this.hpf);
        this.subGain.connect(this.hpf);
        this.noiseGain.connect(this.hpf);

        this.hpf.connect(this.lpf);
        this.lpf.connect(this.chorus);
        this.chorus.connect(this.outputGain);

        // Note: LFO vers pitch sera géré via setLfoToPitch() car PolySynth
        // ne permet pas de connecter directement au detune global

        // Démarrer le noise
        this._startOscillators();
    }

    _startOscillators() {
        this.noise.start();
    }

    _stopOscillators() {
        this.noise.stop();
    }

    connectTo(destination) {
        this.mixerChannel = destination;
        if (this.isPowered) {
            this.outputGain.connect(destination);
        }
    }

    setPower(on) {
        this.isPowered = on;
        if (this.mixerChannel) {
            if (on) {
                this.outputGain.connect(this.mixerChannel);
                if (this.performance.lowPower) {
                    this._startOscillators();
                }
            } else {
                this.outputGain.disconnect();
                this.synth.releaseAll();
                this.synth2.releaseAll();
                this.subSynth.releaseAll();
                if (this.performance.lowPower) {
                    this._stopOscillators();
                }
            }
        }
    }

    getPower() {
        return this.isPowered;
    }

    // Jouer une note (ou plusieurs pour accords)
    triggerAttack(note, time, velocity = 1) {
        if (!this.isPowered) return;

        const notes = Array.isArray(note) ? note : [note];
        const vel = velocity * this._params.volume;

        // Calculer les notes avec octave shift
        const vco1Notes = notes.map(n => this._transposeNote(n, this._params.vco1Octave * 12));
        const vco2Notes = notes.map(n => this._transposeNote(n, this._params.vco2Octave * 12));
        const subNotes = notes.map(n => this._transposeNote(n, -12)); // Une octave en dessous

        this.synth.triggerAttack(vco1Notes, time, vel * this._params.vco1Level);
        this.synth2.triggerAttack(vco2Notes, time, vel * this._params.vco2Level);

        if (this._params.subLevel > 0) {
            this.subSynth.triggerAttack(subNotes, time, vel * this._params.subLevel);
        }

        this.filterEnv.triggerAttack(time);

        if (this._params.noiseLevel > 0) {
            this.noiseEnv.triggerAttack(time);
        }
    }

    triggerRelease(note, time) {
        if (!this.isPowered) return;

        const notes = Array.isArray(note) ? note : [note];

        const vco1Notes = notes.map(n => this._transposeNote(n, this._params.vco1Octave * 12));
        const vco2Notes = notes.map(n => this._transposeNote(n, this._params.vco2Octave * 12));
        const subNotes = notes.map(n => this._transposeNote(n, -12));

        this.synth.triggerRelease(vco1Notes, time);
        this.synth2.triggerRelease(vco2Notes, time);
        this.subSynth.triggerRelease(subNotes, time);

        this.filterEnv.triggerRelease(time);
        this.noiseEnv.triggerRelease(time);
    }

    triggerAttackRelease(note, duration, time, velocity = 1) {
        this.triggerAttack(note, time, velocity);
        this.triggerRelease(note, time + Tone.Time(duration).toSeconds());
    }

    _transposeNote(note, semitones) {
        const freq = Tone.Frequency(note).toFrequency();
        return freq * Math.pow(2, semitones / 12);
    }

    // Setters pour tous les paramètres
    setVco1Wave(wave) {
        this._params.vco1Wave = wave;
        this.synth.set({ oscillator: { type: wave } });
    }

    setVco1Octave(oct) {
        this._params.vco1Octave = oct;
    }

    setVco1Level(level) {
        this._params.vco1Level = level;
        this.vco1Gain.gain.value = level;
    }

    setVco2Wave(wave) {
        this._params.vco2Wave = wave;
        this.synth2.set({ oscillator: { type: wave } });
    }

    setVco2Octave(oct) {
        this._params.vco2Octave = oct;
    }

    setVco2Level(level) {
        this._params.vco2Level = level;
        this.vco2Gain.gain.value = level;
    }

    setVco2Detune(cents) {
        this._params.vco2Detune = cents;
        this.synth2.set({ detune: cents });
    }

    setSubLevel(level) {
        this._params.subLevel = level;
        this.subGain.gain.value = level;
    }

    setNoiseLevel(level) {
        this._params.noiseLevel = level;
        this.noiseGain.gain.value = level;
    }

    setFilterCutoff(freq) {
        this._params.filterCutoff = freq;
        this.lpf.frequency.value = freq;
    }

    setFilterResonance(q) {
        this._params.filterResonance = q;
        this.lpf.Q.value = q;
    }

    setFilterEnvAmount(amount) {
        this._params.filterEnvAmount = amount;
        this.filterEnvGain.gain.value = amount * 5000;
    }

    setHpfCutoff(freq) {
        this._params.hpfCutoff = freq;
        this.hpf.frequency.value = freq;
    }

    setFilterAttack(time) {
        this._params.filterAttack = time;
        this.filterEnv.attack = time;
    }

    setFilterDecay(time) {
        this._params.filterDecay = time;
        this.filterEnv.decay = time;
    }

    setFilterSustain(level) {
        this._params.filterSustain = level;
        this.filterEnv.sustain = level;
    }

    setFilterRelease(time) {
        this._params.filterRelease = time;
        this.filterEnv.release = time;
    }

    setAmpAttack(time) {
        this._params.ampAttack = time;
        this.synth.set({ envelope: { attack: time } });
        this.synth2.set({ envelope: { attack: time } });
        this.subSynth.set({ envelope: { attack: time } });
        this.noiseEnv.attack = time;
    }

    setAmpDecay(time) {
        this._params.ampDecay = time;
        this.synth.set({ envelope: { decay: time } });
        this.synth2.set({ envelope: { decay: time } });
        this.subSynth.set({ envelope: { decay: time } });
        this.noiseEnv.decay = time;
    }

    setAmpSustain(level) {
        this._params.ampSustain = level;
        this.synth.set({ envelope: { sustain: level } });
        this.synth2.set({ envelope: { sustain: level } });
        this.subSynth.set({ envelope: { sustain: level } });
        this.noiseEnv.sustain = level;
    }

    setAmpRelease(time) {
        this._params.ampRelease = time;
        this.synth.set({ envelope: { release: time } });
        this.synth2.set({ envelope: { release: time } });
        this.subSynth.set({ envelope: { release: time } });
        this.noiseEnv.release = time;
    }

    setLfoRate(rate) {
        this._params.lfoRate = rate;
        this.lfo.frequency.value = rate;
    }

    setLfoWave(wave) {
        this._params.lfoWave = wave;
        this.lfo.type = wave;
    }

    setLfoToPitch(amount) {
        this._params.lfoToPitch = amount;
        this.lfoPitchGain.gain.value = amount * 100; // En cents
    }

    setLfoToFilter(amount) {
        this._params.lfoToFilter = amount;
        this.lfoFilterGain.gain.value = amount * 2000;
    }

    setChorusRate(rate) {
        this._params.chorusRate = rate;
        this.chorus.frequency.value = rate;
    }

    setChorusDepth(depth) {
        this._params.chorusDepth = depth;
        this.chorus.depth = depth;
    }

    setChorusMix(mix) {
        this._params.chorusMix = mix;
        this.chorus.wet.value = mix;
    }

    setVolume(vol) {
        this._params.volume = vol;
        this.outputGain.gain.value = vol;
    }

    setPortamento(time) {
        this._params.portamento = time;
        this.synth.set({ portamento: time });
        this.synth2.set({ portamento: time });
        this.subSynth.set({ portamento: time });
    }

    getParams() {
        return { ...this._params };
    }

    setParams(params) {
        Object.entries(params).forEach(([key, value]) => {
            const setter = `set${key.charAt(0).toUpperCase() + key.slice(1)}`;
            if (typeof this[setter] === 'function') {
                this[setter](value);
            }
        });
    }

    dispose() {
        this.synth.dispose();
        this.synth2.dispose();
        this.subSynth.dispose();
        this.noise.dispose();
        this.noiseGain.dispose();
        this.noiseEnv.dispose();
        this.vco1Gain.dispose();
        this.vco2Gain.dispose();
        this.subGain.dispose();
        this.hpf.dispose();
        this.lpf.dispose();
        this.filterEnv.dispose();
        this.filterEnvGain.dispose();
        this.chorus.dispose();
        this.outputGain.dispose();
        this.lfo.dispose();
        this.lfoPitchGain.dispose();
        this.lfoFilterGain.dispose();
        this.lfoPWGain.dispose();
    }
}
